import { formatUserFacingError, getErrorCode, withErrorCode } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { createCallable, functions } from '@/lib/firebase/functions'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { CheckCircle2, AlertTriangle, Clock } from 'lucide-react'

interface PublicLineItem {
  description: string
  qty: number
  unit: 'hour' | 'minute' | 'headshot' | 'flat' | 'each'
  unitPrice: number
  lineTotal: number
  isAddon?: boolean
}

interface PublicEstimate {
  clientName: string
  title: string
  description: string | null
  scheduledDate: string | null
  estimateNote: string | null
  lineItems: PublicLineItem[]
  discountPercent: number
  estimateAmount: number
  depositRequired: boolean
  depositAmount: number
  depositInvoiceSent: boolean
  depositPaid: boolean
  bookingConfirmed: boolean
  acceptedAt: number | null
  expiresAt: number | null
}

const getEstimateFn = createCallable<
  { engagementId: string; token: string },
  PublicEstimate
>(functions, 'getEstimateForAcceptance')

const acceptEstimateFn = createCallable<
  { engagementId: string; token: string },
  {
    success: boolean
    alreadyAccepted: boolean
    estimateAmount: number
    depositRequired: boolean
    autoDepositSent: boolean
    bookingConfirmationQueued: boolean
  }
>(functions, 'acceptEstimate')

function fmtMoney(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

function fmtUnit(unit: PublicLineItem['unit'], qty: number): string {
  if (unit === 'flat') return ''
  if (unit === 'each') return 'each'
  return qty === 1 ? unit : `${unit}s`
}

function fmtDate(ms: number | null): string {
  if (!ms) return ''
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  })
}

type State =
  | { kind: 'loading' }
  | { kind: 'loaded'; estimate: PublicEstimate }
  | { kind: 'error'; message: string; code?: string }
  | { kind: 'accepting'; estimate: PublicEstimate }
  | {
      kind: 'accepted'
      estimate: PublicEstimate
      alreadyAccepted: boolean
      depositInvoiceSent: boolean
      bookingConfirmed: boolean
    }

export function EstimateAcceptPage() {
  const { engagementId, token } = useParams<{ engagementId: string; token: string }>()
  const [state, setState] = useState<State>({ kind: 'loading' })

  const load = useCallback(async () => {
    if (!engagementId || !token) {
      setState({
        kind: 'error',
        message: withErrorCode('This estimate link is missing required information.', 'estimate/invalid-link'),
        code: 'estimate/invalid-link',
      })
      return
    }
    try {
      const { data } = await getEstimateFn({ engagementId, token })
      if (data.acceptedAt) {
        setState({
          kind: 'accepted',
          estimate: data,
          alreadyAccepted: true,
          depositInvoiceSent: data.depositInvoiceSent,
          bookingConfirmed: data.bookingConfirmed,
        })
      } else {
        setState({ kind: 'loaded', estimate: data })
      }
    } catch (err: unknown) {
      const code = getErrorCode(err, 'estimate/load')
      const message = formatUserFacingError(err, 'Failed to load estimate.')
      setState({ kind: 'error', message, code })
    }
  }, [engagementId, token])

  useEffect(() => {
    void load()
  }, [load])

  async function handleAccept() {
    if (state.kind !== 'loaded') return
    setState({ kind: 'accepting', estimate: state.estimate })
    try {
      const { data } = await acceptEstimateFn({ engagementId: engagementId!, token: token! })
      setState({
        kind: 'accepted',
        estimate: state.estimate,
        alreadyAccepted: data.alreadyAccepted,
        depositInvoiceSent: data.autoDepositSent,
        bookingConfirmed: data.bookingConfirmationQueued,
      })
    } catch (err: unknown) {
      const code = getErrorCode(err, 'estimate/accept')
      const message = formatUserFacingError(err, 'Failed to accept estimate.')
      setState({ kind: 'error', message, code })
    }
  }

  if (state.kind === 'loading') {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 space-y-4">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <div className="space-y-2 pt-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (state.kind === 'error') {
    const isExpired = state.code === 'functions/deadline-exceeded' || /expired/i.test(state.message)
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center space-y-6">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-destructive/10 text-destructive">
          {isExpired ? <Clock className="h-8 w-8" /> : <AlertTriangle className="h-8 w-8" />}
        </div>
        <h1 className="text-2xl font-semibold">
          {isExpired ? 'This estimate link has expired' : 'This estimate link is invalid'}
        </h1>
        <p className="text-muted-foreground text-sm">
          {isExpired
            ? "We're sorry — this link is no longer valid. Reply to your estimate email or contact us at "
            : 'Double-check the link from your email, or contact us at '}
          <a href="mailto:hello@onedaydance.com" className="text-foreground underline">
            hello@onedaydance.com
          </a>
          {' '}and we'll send you a fresh one.
        </p>
      </div>
    )
  }

  const estimate = state.estimate
  const subtotal = estimate.lineItems.reduce((s, i) => s + i.lineTotal, 0)
  const discountAmount = Math.round(subtotal * estimate.discountPercent) / 100
  const firstName = (estimate.clientName || '').trim().split(' ')[0] || 'there'
  const scheduledDate = estimate.scheduledDate
    ? fmtDate(new Date(`${estimate.scheduledDate}T00:00:00`).getTime())
    : null

  const acceptedMessage = (() => {
    if (state.kind !== 'accepted') return null
    if (estimate.depositRequired) {
      if (estimate.depositPaid || state.bookingConfirmed) {
        return 'Your booking is confirmed. A calendar invitation and confirmation email are on their way.'
      }
      if (state.depositInvoiceSent) {
        return `Stripe has emailed your ${fmtMoney(estimate.depositAmount)} deposit invoice. Your booking will be confirmed as soon as the deposit is paid.`
      }
      return 'Your estimate is accepted. We are preparing your deposit invoice and will follow up by email shortly.'
    }
    return 'Your booking is confirmed. A calendar invitation and confirmation email are on their way.'
  })()

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:py-16">
      {state.kind === 'accepted' && (
        <div className="mb-8 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-5 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-medium text-emerald-900 dark:text-emerald-100">
              {state.alreadyAccepted ? 'You already accepted this estimate.' : 'Estimate accepted — thank you!'}
            </p>
            <p className="text-sm text-emerald-700/80 dark:text-emerald-200/80">
              {acceptedMessage}
            </p>
          </div>
        </div>
      )}

      <header className="space-y-2 mb-8">
        <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Service Estimate</p>
        <h1 className="text-3xl font-bold">{estimate.title}</h1>
        {estimate.scheduledDate && (
          <p className="text-muted-foreground">Scheduled for {scheduledDate}</p>
        )}
      </header>

      {state.kind === 'loaded' && (
        <p className="text-base mb-8">
          Hi {firstName}, please review the line items below and click <strong>Accept</strong> to confirm.
        </p>
      )}

      {estimate.description && (
        <div className="mb-8 rounded-md border bg-muted/20 p-4 text-sm text-muted-foreground">
          {estimate.description}
        </div>
      )}

      <section className="mb-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Line Items</h2>
        <div className="rounded-lg border overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Item</th>
                <th className="px-4 py-3 font-semibold text-right">Quantity × Rate</th>
                <th className="px-4 py-3 font-semibold text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {estimate.lineItems.map((item, i) => (
                <tr key={i} className="border-t">
                  <td className={`px-4 py-3 ${item.isAddon ? 'pl-8 text-muted-foreground' : 'font-medium'}`}>
                    {item.description}
                    {item.isAddon && <Badge variant="outline" className="ml-2 text-[10px]">add-on</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right text-muted-foreground whitespace-nowrap">
                    {item.qty} {fmtUnit(item.unit, item.qty)} × {fmtMoney(item.unitPrice)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtMoney(item.lineTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-8 rounded-lg border bg-muted/20 p-5 space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="font-mono tabular-nums">{fmtMoney(subtotal)}</span>
        </div>
        {estimate.discountPercent > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Discount ({estimate.discountPercent}%)</span>
            <span className="font-mono tabular-nums text-destructive">−{fmtMoney(discountAmount)}</span>
          </div>
        )}
        <Separator />
        <div className="flex items-center justify-between text-base font-semibold pt-1">
          <span>Total</span>
          <span className="font-mono tabular-nums">{fmtMoney(estimate.estimateAmount)}</span>
        </div>
      </section>

      {estimate.estimateNote && (
        <p className="mb-8 text-sm text-muted-foreground italic">Note: {estimate.estimateNote}</p>
      )}

      {state.kind === 'loaded' && (
        <section className="mb-8 rounded-lg border border-primary/20 bg-primary/5 p-5">
          <h2 className="text-sm font-semibold mb-1">What happens after you accept</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {estimate.depositRequired
              ? `Stripe will email you a ${fmtMoney(estimate.depositAmount)} deposit invoice. Once the deposit is paid, we'll confirm your booking and send a calendar invitation. The remaining balance is invoiced after the service.`
              : `No deposit is required. We'll confirm your booking and send a calendar invitation, then invoice the full balance after the service.`}
          </p>
        </section>
      )}

      {state.kind === 'loaded' && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
          {estimate.expiresAt && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              This link expires on {fmtDate(estimate.expiresAt)}
            </p>
          )}
          <Button size="lg" onClick={() => void handleAccept()} className="w-full sm:w-auto">
            Accept Estimate
          </Button>
        </div>
      )}

      {state.kind === 'loaded' && (
        <p className="mt-3 text-xs text-muted-foreground text-center sm:text-right">
          By accepting, you approve the scope and pricing shown above.
        </p>
      )}

      {state.kind === 'accepting' && (
        <div className="pt-4 border-t flex justify-end">
          <Button size="lg" disabled>Accepting…</Button>
        </div>
      )}
    </div>
  )
}
